import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Twitter, Instagram, Facebook, Linkedin, Loader2, ArrowRight, LogOut, History } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { ForcefieldLogo } from "@/components/ForcefieldLogo";

export default function AnalysisPage() {
  const navigate = useNavigate();
  const { token, logout, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [handles, setHandles] = useState({
    twitter: "",
    instagram: "",
    facebook: "",
    linkedin: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const hasHandle = Object.values(handles).some(h => h.trim());
    if (!hasHandle) {
      toast.error("Please enter at least one social media handle");
      return;
    }

    setLoading(true);
    setScanning(true);

    try {
      const cleanHandles = {
        twitter: handles.twitter.replace(/^@/, '').trim() || null,
        instagram: handles.instagram.replace(/^@/, '').trim() || null,
        facebook: handles.facebook.trim() || null,
        linkedin: handles.linkedin.trim() || null
      };

      const response = await fetch(`${API}/analysis`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ handles: cleanHandles })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Analysis failed");
      }

      toast.success("Analysis complete!");
      navigate(`/dashboard/${data.id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
      setScanning(false);
    }
  };

  const platforms = [
    { key: "twitter", icon: Twitter, label: "Twitter", placeholder: "@username" },
    { key: "instagram", icon: Instagram, label: "Instagram", placeholder: "@username" },
    { key: "facebook", icon: Facebook, label: "Facebook", placeholder: "Profile name or URL" },
    { key: "linkedin", icon: Linkedin, label: "LinkedIn", placeholder: "Profile name or URL" }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="analysis-logo-link">
            <ForcefieldLogo className="w-10 h-10" />
            <span className="font-outfit font-bold text-xl text-white tracking-tight">Social <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Forcefield</span></span>
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/history">
              <Button variant="ghost" className="text-gray-400 hover:text-white gap-2" data-testid="analysis-history-btn">
                <History className="w-4 h-4" />
                History
              </Button>
            </Link>
            <span className="text-gray-500 text-sm hidden sm:block">{user?.name}</span>
            <Button 
              variant="ghost" 
              onClick={logout}
              className="text-gray-400 hover:text-white gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </nav>
        </div>
      </header>

      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-violet-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-1/4 w-[300px] h-[300px] bg-fuchsia-600/5 rounded-full blur-[100px]" />
      </div>

      {/* Main content */}
      <main className="pt-32 pb-16 px-6 relative">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm text-violet-400 uppercase tracking-widest mb-4">/activate protection/</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
              Enter Your Handles
            </h1>
            <p className="text-gray-400 text-lg">
              Provide your social media usernames to activate your forcefield
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className={`p-8 rounded-2xl bg-white/[0.02] border border-white/5 relative overflow-hidden ${scanning ? 'animate-pulse-glow' : ''}`}>
              <div className="space-y-6">
                {platforms.map((platform) => (
                  <div key={platform.key} className="space-y-2">
                    <Label htmlFor={platform.key} className="text-gray-300 flex items-center gap-2">
                      <platform.icon className="w-4 h-4 text-gray-500" />
                      {platform.label}
                    </Label>
                    <Input
                      id={platform.key}
                      type="text"
                      placeholder={platform.placeholder}
                      value={handles[platform.key]}
                      onChange={(e) => setHandles({ ...handles, [platform.key]: e.target.value })}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 h-12 focus:border-violet-500/50 focus:ring-violet-500/20 rounded-xl font-mono"
                      disabled={loading}
                      data-testid={`${platform.key}-input`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white h-14 text-lg font-medium rounded-xl group"
              data-testid="analyze-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Activating Forcefield...
                </>
              ) : (
                <>
                  Activate Security Scan
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

          {/* Info */}
          <div className="mt-12 p-6 rounded-2xl bg-white/[0.02] border border-white/5">
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">What We Analyze</h3>
            <ul className="space-y-3 text-gray-400 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-violet-400 rounded-full mt-2" />
                <span>Privacy settings and public profile exposure</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-violet-400 rounded-full mt-2" />
                <span>Exposed personal information (email, phone, location)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-violet-400 rounded-full mt-2" />
                <span>Account vulnerability indicators and 2FA status</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-violet-400 rounded-full mt-2" />
                <span>Cross-platform security patterns and correlation risks</span>
              </li>
            </ul>
          </div>

          {/* Powered by */}
          <p className="mt-8 text-center text-gray-600 text-sm">
            powered by <a href="https://agentseven.ai" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">AgentSeven.ai</a>
          </p>
        </div>
      </main>
    </div>
  );
}
